import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { renderToString } from 'react-dom/server'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { SwiperOptions, Navigation, Pagination } from 'swiper/core'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import classNames from 'classnames'
import kebabCase from 'lodash.kebabcase'

import {
  mq,
  mqBreakpoints,
  MVPColorIdTokenType,
  IMVPColorTokenProps,
  MVP_COLOR_TOKENS,
} from 'marketing-site/src/library/utils'
import CarouselLeftArrow from 'marketing-site/src/mvp/library/images/carousel-left-arrow.svg'
import CarouselRightArrow from 'marketing-site/src/mvp/library/images/carousel-right-arrow.svg'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import { SpotlightDrawersSlide } from 'marketing-site/src/mvp/library/elements/SpotlightDrawersSlide'
import { Text } from 'marketing-site/src/library/elements/Text'
import { IProps } from './index'

interface IPaginationDesktopTheme {
  inactiveBullet: {
    titleColor: IMVPColorTokenProps['hex']
    subheadColor: IMVPColorTokenProps['hex']
    backgroundColor: IMVPColorTokenProps['hex']
  }
  activeBullet: {
    titleColor: IMVPColorTokenProps['hex']
    subheadColor: IMVPColorTokenProps['hex']
    backgroundColor: IMVPColorTokenProps['hex']
  }
}

const getPaginationDesktopTheme = (bgColor: MVPColorIdTokenType): IPaginationDesktopTheme => {
  switch (bgColor) {
    case 'slate':
    case 'blue':
    case 'deepTeal':
      return {
        inactiveBullet: {
          titleColor: MVP_COLOR_TOKENS['paper'].hex,
          subheadColor: MVP_COLOR_TOKENS['neutral200'].hex,
          backgroundColor: 'transparent',
        },
        activeBullet: {
          titleColor: MVP_COLOR_TOKENS['slate'].hex,
          subheadColor: MVP_COLOR_TOKENS['neutral700'].hex,
          backgroundColor: MVP_COLOR_TOKENS['paper'].hex,
        },
      }
    case 'stone':
    case 'sky':
      return {
        inactiveBullet: {
          titleColor: MVP_COLOR_TOKENS['slate'].hex,
          subheadColor: MVP_COLOR_TOKENS['neutral700'].hex,
          backgroundColor: 'transparent',
        },
        activeBullet: {
          titleColor: MVP_COLOR_TOKENS['slate'].hex,
          subheadColor: MVP_COLOR_TOKENS['neutral700'].hex,
          backgroundColor: MVP_COLOR_TOKENS['paper'].hex,
        },
      }
    case 'transparent':
    default:
      return {
        inactiveBullet: {
          titleColor: MVP_COLOR_TOKENS['slate'].hex,
          subheadColor: MVP_COLOR_TOKENS['neutral700'].hex,
          backgroundColor: 'transparent',
        },
        activeBullet: {
          titleColor: MVP_COLOR_TOKENS['slate'].hex,
          subheadColor: MVP_COLOR_TOKENS['neutral700'].hex,
          backgroundColor: MVP_COLOR_TOKENS['stone'].hex,
        },
      }
  }
}

export const SpotlightDrawers = ({
  layout = 'media-right',
  eyebrow,
  title,
  cta,
  slides,
  backgroundColor = 'transparent',
}: IProps): React.ReactElement => {
  const { translate } = useTranslate()

  const swiperRef = useRef<SwiperCore>()
  const prevRef = useRef<HTMLButtonElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)
  // TODO remove this re-rendering hack once Swiper is upgraded
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true)

  const [navigationTop, setNavigationTop] = useState(0)
  const [paginationTopMobile, setPaginationTopMobile] = useState(0)
  const [paginationTopTablet, setPaginationTopTablet] = useState(0)
  const [paginationDesktopTheme, setPaginationDesktopTheme] = useState<IPaginationDesktopTheme>({
    inactiveBullet: {
      titleColor: MVP_COLOR_TOKENS['slate'].hex,
      subheadColor: MVP_COLOR_TOKENS['neutral700'].hex,
      backgroundColor: 'transparent',
    },
    activeBullet: {
      titleColor: MVP_COLOR_TOKENS['slate'].hex,
      subheadColor: MVP_COLOR_TOKENS['neutral700'].hex,
      backgroundColor: MVP_COLOR_TOKENS['stone'].hex,
    },
  })

  const setSwiperRef = useCallback((swiperInstance) => {
    if (swiperInstance) {
      swiperRef.current = swiperInstance
    }
  }, [])

  useEffect(() => {
    isFirstRender && setIsFirstRender(false)
    setTimeout(() => swiperRef.current?.updateAutoHeight(), 5000)
  }, [isFirstRender])

  const settings: SwiperOptions = {
    loop: true,
    autoHeight: true,
    navigation: {
      nextEl: nextRef.current,
      prevEl: prevRef.current,
    },
    pagination: {
      el: '.spotlight-drawers-pagination',
      bulletClass: 'spotlight-drawers-pagination__bullet',
      bulletActiveClass: 'spotlight-drawers-pagination__bullet--active',
      clickable: true,
      type: 'bullets',
      renderBullet: function (index, className) {
        const classes = classNames(className, {
          'spotlight-drawers-pagination__bullet--no-icon': !slides[index].icon,
        })
        return `<button class="${classes}">
          ${
            !!slides[index].icon
              ? `<div class="spotlight-drawers-pagination__icon">${renderToString(
                  slides[index].icon as ReactElement,
                )}</div>`
              : ''
          }
          <h3 class="spotlight-drawers-pagination__title">
            <span class="text-size-body-bold">
              ${slides[index].title}
            </span>
            <svg class="spotlight-drawers-pagination__chevron" width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8.70711 11.2071C8.31658 11.5976 7.68342 11.5976 7.29289 11.2071L4.29289 8.20711C3.90237 7.81658 3.90237 7.18342 4.29289 6.79289C4.68342 6.40237 5.31658 6.40237 5.70711 6.79289L8 9.08579L10.2929 6.79289C10.6834 6.40237 11.3166 6.40237 11.7071 6.79289C12.0976 7.18342 12.0976 7.81658 11.7071 8.20711L8.70711 11.2071Z" fill="currentColor"/>
            </svg>
          </h3>
          <div class="spotlight-drawers-pagination__subhead">
            <span class="text-size-body">${documentToHtmlString(slides[index].subhead)}</span>
          </div>
        </button>`
      },
    },
    resizeObserver: true,
    observer: true,
    observeParents: true,
  }

  SwiperCore.use([Navigation, Pagination])

  useEffect(() => {
    const swiperCurrentRef = swiperRef.current
    if (swiperCurrentRef) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // @ts-ignore
          setNavAndPagPosition(entry.target.swiper)
        }
      })
      resizeObserver.observe(swiperCurrentRef.$el[0])

      // Re-set swiper wrapper height on initialization.
      setTimeout(() => swiperRef.current?.updateAutoHeight(), 0)
    }
  }, [])

  useEffect(() => {
    setPaginationDesktopTheme(() => getPaginationDesktopTheme(backgroundColor))
  }, [backgroundColor])

  const setNavAndPagPosition = (event: SwiperCore) => {
    if (!event || event.destroyed) return

    const activeIndex = event.activeIndex
    const bodyWidth = document.body.clientWidth
    const sliderBox: HTMLDivElement | null = document.querySelector('.spotlight-drawers__slider')
    const mediaBox: HTMLDivElement | null = event.slides[activeIndex]?.querySelector(
      '.spotlight-drawers-slide__media',
    )
    const paginationBox: HTMLDivElement | null = document.querySelector(
      '.spotlight-drawers-pagination',
    )

    if (mediaBox) {
      setNavigationTop(mediaBox.offsetHeight / 2)
    }

    if (sliderBox && mediaBox && bodyWidth < mqBreakpoints.tablet) {
      setPaginationTopMobile(sliderBox.offsetTop + mediaBox.offsetHeight + 24)
    }

    if (
      sliderBox &&
      mediaBox &&
      bodyWidth >= mqBreakpoints.tablet &&
      bodyWidth < mqBreakpoints.desktop
    ) {
      setPaginationTopTablet(sliderBox.offsetTop + mediaBox.offsetHeight + 32)
    }

    if (paginationBox && bodyWidth >= mqBreakpoints.desktop) {
      setTimeout(() => {
        paginationBox.style.minHeight = `${paginationBox.offsetHeight}px`
      }, 2000)
    }
  }

  const classes = classNames('spotlight-drawers', 'grid-layout', {
    [`spotlight-drawers--${layout}`]: layout,
    [`mvp-background-color-${kebabCase(backgroundColor)}`]: backgroundColor,
  })

  return (
    <>
      <div className={classes} data-testid="spotlight-drawers">
        <div className="spotlight-drawers__inner">
          <header className="spotlight-drawers__header">
            {eyebrow && (
              <div className="spotlight-drawers__eyebrow">
                <Text size="eyebrow">{eyebrow}</Text>
              </div>
            )}
            <h2 className="spotlight-drawers__title">
              <Text size="heading-3">{title}</Text>
            </h2>
          </header>
          {cta && <div className="spotlight-drawers__cta">{cta}</div>}
          <div className="spotlight-drawers__slider">
            <Swiper
              {...settings}
              onInit={(swiper) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                // eslint-disable-next-line no-param-reassign
                swiper.params.navigation.prevEl = prevRef.current
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                // eslint-disable-next-line no-param-reassign
                swiper.params.navigation.nextEl = nextRef.current
                swiper.navigation.update()
              }}
              onSwiper={(swiperInstance) => setSwiperRef(swiperInstance)}
              onObserverUpdate={(event) => setNavAndPagPosition(event)}
            >
              {slides.map((slide) => (
                <SwiperSlide key={slide.title}>
                  <SpotlightDrawersSlide {...slide} />
                </SwiperSlide>
              ))}
            </Swiper>
            <button className="swiper-button swiper-button-prev" ref={prevRef}>
              <span className="screen-readers-only">{translate('Previous slide')}</span>
            </button>
            <button className="swiper-button swiper-button-next" ref={nextRef}>
              <span className="screen-readers-only">{translate('Next slide')}</span>
            </button>
          </div>
          <div className="spotlight-drawers-pagination"></div>
        </div>
      </div>

      <style global jsx>{`
        @media (min-width: 1024px) {
          .spotlight-drawers {
            .spotlight-drawers-pagination__bullet {
              padding: 0.75rem 1.5rem;

              &--active {
                padding: 1.25rem 1.5rem 1.5rem;
              }
            }
          }
        }
      `}</style>

      <style jsx>
        {`
          .spotlight-drawers {
            &__inner {
              align-items: center;
            }

            &__header {
              grid-column: 1/-1;
              text-align: center;
            }

            &__eyebrow {
              margin-bottom: 1rem;
            }

            &__cta {
              grid-column: 1/-1;
              display: flex;
              flex-direction: column;
              justify-content: center;
              margin-top: 2rem;
            }

            &__slider {
              grid-column: 1/-1;
              position: relative;
              // Will prevent the element from exceeding the grid’s limits.
              min-width: 0;
              margin-top: 2.5rem;
            }

            :global(.swiper-wrapper) {
              will-change: transform, height;
            }

            &-pagination {
              display: flex;
              flex-wrap: wrap;
              gap: 0.75rem;
              justify-content: center;
              position: absolute;
              top: ${paginationTopMobile}px;
              bottom: auto;
              left: 50%;
              transform: translateX(-50%);
              transition: top 0.24s;
              z-index: 10;
            }

            :global(.spotlight-drawers-pagination__bullet) {
              display: inline-block;
              width: 0.5rem;
              height: 0.5rem;
              border-radius: 50%;
              margin: 0;
              opacity: 1;
              background: $neutral-300;
              font-size: 0.75rem;
              text-indent: -9999px;
              transition: background-color 0.32s, color 0.32s, padding 0.32s;
              cursor: pointer;
            }

            :global(.spotlight-drawers-pagination__bullet--active) {
              background: $mvp-blue;
              color: $paper;
            }

            :global(.spotlight-drawers-pagination__bullet--active svg) {
              transform: rotateX(180deg);
              transition: transform 0.24s;
            }
          }

          .swiper-button {
            height: 2.5rem;
            width: 2.5rem;
            top: ${navigationTop}px;
            border: 2px solid $slate;
            border-radius: 50%;
            background-color: $white;
            transform-origin: center;
            transition: transform 0.24s;

            &::after {
              font-size: inherit;
              line-height: 0;
            }
          }

          .swiper-button-prev {
            transform: translateX(-50%);
            left: 0.5rem;

            &::after {
              content: url(${CarouselLeftArrow});
            }

            &:focus,
            &:hover {
              transform: translateX(-50%) scale(108%);
            }
          }

          .swiper-button-next {
            transform: translateX(50%);
            right: 0.5rem;

            &::after {
              content: url(${CarouselRightArrow});
            }

            &:focus,
            &:hover {
              transform: translateX(50%) scale(108%);
            }
          }

          .screen-readers-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border-width: 0;
          }

          @media (${mq.tablet}) {
            .spotlight-drawers {
              &__header {
                grid-column: 2/-2;
              }

              &__eyebrow {
                margin-bottom: 1.25rem;
              }

              &__cta {
                margin-top: 2.5rem;
                flex-direction: row;
              }

              &__slider {
                margin-top: 3.5rem;
              }

              &-pagination {
                top: ${paginationTopTablet}px;
              }
            }

            .swiper-button {
              height: 3.125rem;
              width: 3.125rem;
            }

            .swiper-button-prev {
              // Calculate button position based on columns structure.
              left: calc((100% - 1.75rem * 11) / 12 * 2 + 1.75rem * 2 - 0.5rem);
            }

            .swiper-button-next {
              // Calculate button position based on columns structure.
              right: calc((100% - 1.75rem * 11) / 12 * 2 + 1.75rem * 2 - 0.5rem);
            }
          }

          @media (${mq.desktop}) {
            .spotlight-drawers {
              &__header {
                align-self: end;
                text-align: left;
              }

              &__cta {
                grid-row: 3;
                justify-content: start;
              }

              &__slider {
                grid-row: 1 / ${cta ? 4 : 3};
                margin-top: 0;
              }

              :global(.swiper-container) {
                border-radius: 1.5rem;
                background-color: $white;
                overflow: hidden;
              }

              &-pagination {
                align-self: start;
                margin-top: 2.5rem;
                grid-row: 2;
                top: auto;
                left: auto;
                transform: none;
                position: relative;
                display: flex;
                flex-direction: column;
                row-gap: 0.5rem;
                justify-content: start;
                text-align: left;
              }

              :global(.spotlight-drawers-pagination__bullet) {
                display: grid;
                grid-template-columns: auto 1fr;
                grid-column-gap: 0.75rem;
                align-items: center;
                width: auto;
                height: auto;
                border-radius: 0.5rem;
                background-color: ${paginationDesktopTheme?.inactiveBullet.backgroundColor};
                text-indent: 0;
              }

              :global(.spotlight-drawers-pagination__bullet--no-icon) {
                grid-template-columns: 1fr;
              }

              :global(.spotlight-drawers-pagination__bullet--active) {
                background-color: ${paginationDesktopTheme?.activeBullet.backgroundColor};
                transition: background-color 0.4s ease 0.4s, color 0.4s ease 0.4s,
                  padding 0.4s ease 0.4s;
              }

              :global(.spotlight-drawers-pagination__bullet--active.spotlight-drawers-pagination__bullet--no-icon) {
                padding: 1.5rem;
              }

              :global(.spotlight-drawers-pagination__icon) {
                grid-row: 1;
                max-width: 2rem;
              }

              :global(.spotlight-drawers-pagination__icon noscript + picture) {
                display: none;
              }

              :global(.spotlight-drawers-pagination__title) {
                grid-row: 1;
                display: flex;
                gap: 0.25rem;
                align-items: center;
              }

              :global(.spotlight-drawers-pagination__chevron) {
                grid-row: 1;
                display: flex;
                align-items: center;
              }

              :global(.spotlight-drawers-pagination__chevron path) {
                fill: ${paginationDesktopTheme?.inactiveBullet.titleColor};
              }

              // prettier-ignore
              :global(.spotlight-drawers-pagination__bullet:focus:not(.spotlight-drawers-pagination__bullet-active) .spotlight-drawers-pagination__chevron path),
              :global(.spotlight-drawers-pagination__bullet:hover:not(.spotlight-drawers-pagination__bullet-active) .spotlight-drawers-pagination__chevron path) {
                fill: ${paginationDesktopTheme?.activeBullet.titleColor};
              }

              // prettier-ignore
              :global(.spotlight-drawers-pagination__bullet--active .spotlight-drawers-pagination__chevron path) {
                fill: ${paginationDesktopTheme?.activeBullet.titleColor};
              }

              :global(.spotlight-drawers-pagination__subhead) {
                grid-column: 2;
                grid-row: 2;
                max-height: 0;
                overflow: hidden;
                transition: max-height 0.4s, margin 0.4s;
              }

              // prettier-ignore
              :global(.spotlight-drawers-pagination__bullet--no-icon .spotlight-drawers-pagination__subhead) {
                grid-column: 1;
              }

              // prettier-ignore
              :global(.spotlight-drawers-pagination__bullet--active .spotlight-drawers-pagination__subhead) {
                max-height: 6.25rem;
                margin-top: 0.75rem;
                transition: max-height 0.4s ease 0.4s, margin 0.4s ease 0.4s;
              }

              :global(.spotlight-drawers-pagination__bullet .text-size-body-bold) {
                color: ${paginationDesktopTheme?.inactiveBullet.titleColor};
              }

              :global(.spotlight-drawers-pagination__bullet .text-size-body) {
                color: ${paginationDesktopTheme?.inactiveBullet.subheadColor};
              }

              :global(.spotlight-drawers-pagination__bullet:focus:not(.spotlight-drawers-pagination__bullet-active)),
              :global(.spotlight-drawers-pagination__bullet:hover:not(.spotlight-drawers-pagination__bullet-active)) {
                background-color: ${paginationDesktopTheme?.activeBullet.backgroundColor};
              }

              // prettier-ignore
              :global(.spotlight-drawers-pagination__bullet:focus:not(.spotlight-drawers-pagination__bullet--active) .text-size-body-bold),
              :global(.spotlight-drawers-pagination__bullet:hover:not(.spotlight-drawers-pagination__bullet--active) .text-size-body-bold) {
                color: ${paginationDesktopTheme?.activeBullet.titleColor};
              }

              // prettier-ignore
              :global(.spotlight-drawers-pagination__bullet:focus:not(.spotlight-drawers-pagination__bullet--active) .text-size-body),
              :global(.spotlight-drawers-pagination__bullet:hover:not(.spotlight-drawers-pagination__bullet--active) .text-size-body) {
                color: ${paginationDesktopTheme?.activeBullet.subheadColor};
              }

              :global(.spotlight-drawers-pagination__bullet--active .text-size-body-bold) {
                color: ${paginationDesktopTheme?.activeBullet.titleColor};
              }

              :global(.spotlight-drawers-pagination__bullet--active .text-size-body) {
                color: ${paginationDesktopTheme?.activeBullet.subheadColor};
              }

              &--media-right {
                .spotlight-drawers__header {
                  grid-column: 1/7;
                }

                .spotlight-drawers__cta {
                  grid-column: 1/7;
                }

                .spotlight-drawers__slider {
                  grid-column: 7/-1;
                }

                .spotlight-drawers-pagination {
                  grid-column: 1/7;
                }
              }

              &--media-left {
                .spotlight-drawers__header {
                  grid-column: 7/-1;
                }

                .spotlight-drawers__cta {
                  grid-column: 7/-1;
                }

                .spotlight-drawers__slider {
                  grid-column: 1/7;
                }

                .spotlight-drawers-pagination {
                  grid-column: 7/-1;
                }
              }
            }

            .swiper-button-prev,
            .swiper-button-next {
              display: none;
            }
          }

          @media (${mq.desktopLg}) {
            .spotlight-drawers {
              &--media-right {
                .spotlight-drawers__header {
                  grid-column: 1/6;
                }

                .spotlight-drawers__cta {
                  grid-column: 1/6;
                }

                .spotlight-drawers__slider {
                  grid-column: 7/-1;
                }

                .spotlight-drawers-pagination {
                  grid-column: 1/6;
                }
              }

              &--media-left {
                .spotlight-drawers__header {
                  grid-column: 7/-1;
                }

                .spotlight-drawers__cta {
                  grid-column: 7/-1;
                }

                .spotlight-drawers__slider {
                  grid-column: 1/6;
                }

                .spotlight-drawers-pagination {
                  grid-column: 7/-1;
                }
              }
            }
          }
        `}
      </style>
    </>
  )
}
